import { MailOutlined } from '@ant-design/icons';
import { GrayColors, typographyStyle, Input, Button, Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { useLoading } from '@revenuemarket/react';
import { Typography } from '@revenuemarket/ui';
import { Form } from 'antd';
import { Rule } from 'antd/es/form';
import * as yup from 'yup';

import { COMMON_YUP } from '~constants/yup-scheme';
import { css } from '~libs/emotion';
import { AuthModel } from '~models/auth';

import { ResetPasswordEmailVerificationStepModel } from './ResetPasswordEmailVertificationStep';

interface ResetPasswordInfoStepForm extends AuthModel.ResetPasswordRequest {
  passwordConfirm: string;
}

const ResetPasswordInfoStepScheme = yup.object({
  email: COMMON_YUP.email,
  password: COMMON_YUP.password,
});

const getSchemeRule = (field: string): Rule => ({
  validator: async (_, value) => {
    await ResetPasswordInfoStepScheme.validateAt(field, { [field]: value });
  },
});

export interface ResetPasswordInfoStepModel extends ResetPasswordEmailVerificationStepModel {
  passcode: string;
}

interface Props {
  model: ResetPasswordInfoStepModel;
  onFinish: (values: ResetPasswordInfoStepForm) => void | Promise<void>;
}

const ResetPasswordInfoStep = ({ model, onFinish: onFinishProp }: Props) => {
  const [form] = Form.useForm<ResetPasswordInfoStepForm>();

  const [isLoading, startTransaction] = useLoading();

  const onFinish = (values: ResetPasswordInfoStepForm) => {
    startTransaction(() => Promise.resolve(onFinishProp(values)));
  };

  return (
    <Flex direction="column" gap={12}>
      <div style={{ paddingBottom: 12 }}>
        <Typography
          variant="body1"
          color={GrayColors.gray900}
          style={{
            ...typographyStyle['24_600'],
          }}
        >
          비밀번호 설정
        </Typography>
      </div>

      <Form
        form={form}
        onFinish={onFinish}
        initialValues={{ ...model, agreements: [] }}
        className={css`
          width: 440px;

          @media (max-width: 768px) {
            width: 100%;
            max-width: 440px;
          }
        `}
      >
        <Flex direction="column" gap={12}>
          <Form.Item name="email" rules={[getSchemeRule('email')]} style={{ display: 'none' }}>
            <Input
              disabled
              size="large"
              type="email"
              placeholder="가입하신 회사 이메일"
              prefix={<MailOutlined style={{ color: theme.colorTokens.iconDefault }} />}
            />
          </Form.Item>

          <Form.Item name="passcode" style={{ display: 'none' }}>
            <Input disabled type="hidden" />
          </Form.Item>

          <Form.Item name="password" style={{ margin: 0 }}>
            <Input inputSize="xlarge" passwordType={true} placeholder="비밀번호를 입력해주세요" />
          </Form.Item>

          <Typography
            variant="body1"
            style={{
              ...typographyStyle['16_400'],
              color: GrayColors.gray900,
            }}
          >
            8자리 이상 비밀번호를 입력해주세요
          </Typography>

          <Form.Item<ResetPasswordInfoStepForm> noStyle shouldUpdate>
            {({ getFieldsValue }) => {
              const value = getFieldsValue();
              const valid = (() => {
                try {
                  ResetPasswordInfoStepScheme.validateSync(value);
                  return true;
                } catch {
                  return false;
                }
              })();

              return (
                <Button
                  buttonType="primary"
                  buttonSize="xlarge"
                  onClick={form.submit}
                  disable={!valid}
                  loading={isLoading}
                >
                  변경하기
                </Button>
              );
            }}
          </Form.Item>
        </Flex>
      </Form>
    </Flex>
  );
};

export default ResetPasswordInfoStep;
